/**
 * TerminalNode
 *
 * Represents a final outcome state in the workflow. Every decision node leads
 * to one or more terminal nodes — they are the leaf nodes of the flowchart
 * and have no outgoing connections.
 *
 * Three variants are supported, each with its own icon and color scheme:
 *
 *   - "clear"       → green verified icon  — the check passed successfully
 *   - "needsReview" → red alert icon       — the result requires human review
 *   - "checkError"  → red alert icon       — a background check service error occurred
 *
 * Example terminal nodes in the flowchart:
 *   - "Clear"             (identity proof passed, no issues found)
 *   - "Needs Review"      (watchlist record found, liveness failed, etc.)
 *   - "End - Check Error" (authoritative DB returned an error)
 *
 * Handles (target-only — terminals only receive edges, never send them):
 *   - target-left:  receives an edge arriving from the right (e.g. from a decision node)
 *   - target-top:   receives an edge arriving from above (e.g. Yes branch going straight down)
 *   - target-right: receives an edge arriving from the left (e.g. from a left-side decision)
 *
 * The variant determines both the icon and the background/border color via VARIANT_CONFIG.
 *
 * Data props:
 *   - label:   display text (e.g. "Clear", "Needs Review", "End - Check Error")
 *   - variant: visual style key ('clear' | 'needsReview' | 'checkError')
 */
import React from 'react';

import { Box, Typography } from '@mui/material';
import { Handle, Position } from '@xyflow/react';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import styles from '../../FlowChartCanvas.module.css';

import { ReactComponent as AlertIcon } from 'asset/IconsPack/at_alert-triangle.svg';
import { ReactComponent as VerifiedIcon } from 'asset/IconsPack/at_check-verified-01.svg';

const VARIANT_CONFIG = {
    clear: {
        icon: VerifiedIcon,
        className: styles.terminalNodeClear,
        color: '#12B76A',
    },
    needsReview: {
        icon: AlertIcon,
        className: styles.terminalNodeWarning,
        color: '#F56363',
    },
    checkError: {
        icon: AlertIcon,
        className: styles.terminalNodeError,
        color: '#F56363',
    },
};

function TerminalNode({ data }) {
    const config = VARIANT_CONFIG[data.variant] || VARIANT_CONFIG.clear;
    const Icon = config.icon;

    return (
        <Box className={clsx(styles.terminalNode, config.className)}>
            <Icon className={styles.terminalNodeIcon} style={{ color: config.color }} />
            <Typography component="span" className={styles.terminalLabel}>
                {data.label}
            </Typography>

            {/* Terminals are leaf nodes — they only receive edges, never send them */}
            <Handle
                type="target"
                position={Position.Left}
                id="target-left"
                style={{ opacity: 0 }}
            />
            <Handle type="target" position={Position.Top} id="target-top" style={{ opacity: 0 }} />
            <Handle
                type="target"
                position={Position.Right}
                id="target-right"
                style={{ opacity: 0 }}
            />
        </Box>
    );
}

TerminalNode.propTypes = {
    data: PropTypes.shape({
        label: PropTypes.string.isRequired,
        variant: PropTypes.oneOf(['clear', 'needsReview', 'checkError']).isRequired,
    }).isRequired,
};

export default TerminalNode;
